/**
 * The two side columns of the v2.1 domain page. Left, "Quản lý tên miền": whose domain it is
 * (registrant, account, channel, dossier, policy, dates). Right, "Thao tác tên miền": every action
 * (renewal first), the switches staff look at (auto-renew, transfer lock, domain hold, update lock),
 * the renewal preconditions from the domain's own data, and the record's version with a link to
 * its history.
 */

import { ArrowLeft, Check, ChevronRight, CircleAlert, Clock, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NavigationLink } from '@/lib/navigationMemory';
import type { DomainDetail, RegistryTransaction } from '../../api';
import { Badge } from '../../components/Badge';
import { formatDate, formatDateTime } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { DomainActions, enabledActions } from './DomainActions';
import { unresolvedCount } from './ribbon';
import { CustomerRef } from './refs';
import { DOSSIER, LIFECYCLE, StatusBadge, useStatusLabel } from './status';
import { domainTabUrl } from './tabs/moved';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

/** A white card of a side column: small caps title, optional subtitle. */
export function RailCard({ title, subtitle, children, className }: { title: ReactNode; subtitle?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('min-w-0 rounded-xl border bg-card', className)}>
      <header className="border-b px-4 pt-4 pb-3">
        <h2 className="text-[13.5px] font-semibold">{title}</h2>
        {subtitle ? <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{subtitle}</p> : null}
      </header>
      <div className="px-4 py-3.5">{children}</div>
    </section>
  );
}

function Fact({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0 border-b py-2.5 last:border-b-0">
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-[12.5px] font-medium break-words">{children}</dd>
    </div>
  );
}

/** "Quản lý tên miền": the parties and the dossier, on every tab. */
export function ContextRail({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const tr = td as unknown as Lookup;
  const L = useLabels();
  const d = detail.domain;
  const x = detail.dossier;
  return (
    <aside className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-20" aria-label={t('context.title')} data-context-rail>
      <section className="min-w-0 rounded-xl border bg-card px-4 py-4">
        <div className="text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{t('context.title')}</div>
        <div className="flex items-start gap-2.5 border-b py-4">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
            <UserRound className="size-4.5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <Link to={domainTabUrl(d.ascii_name, 'subject')} className="block text-[13px] font-semibold break-words hover:underline">
              {d.subject_display_name ?? t('context.noSubject')}
            </Link>
            {d.subject_type ? <span className="mt-1 block text-[11px] text-muted-foreground">{tr(`subject.types.${d.subject_type}`, { defaultValue: d.subject_type })}</span> : null}
          </div>
        </div>
        <dl>
          <Fact label={t('context.customer')}>
            <CustomerRef id={d.customer_id} name={d.customer_name} />
          </Fact>
          <Fact label={t('context.dossier')}>
            <Link to={domainTabUrl(d.ascii_name, 'dossier')} className="inline-flex flex-wrap items-center gap-1.5 hover:underline">
              {x ? td('common.version', { n: x.dossier_version }) : null}
              <StatusBadge map={DOSSIER} value={x?.status ?? d.dossier_state} />
            </Link>
          </Fact>
          {x?.policy_key ? <Fact label={t('context.policy')}>{L.policy(x.policy_key, x.policy_version)}</Fact> : null}
          <Fact label={t('context.registered')}>{formatDate(d.registry_created_at ?? d.created_at)}</Fact>
        </dl>
        {d.current_subject_id ? (
          <Link to={`/subjects/${encodeURIComponent(d.current_subject_id)}`} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            <ChevronRight className="size-3.5" aria-hidden="true" /> {t('context.openSubject')}
          </Link>
        ) : null}
      </section>
      <Button asChild variant="outline" size="sm" className="text-muted-foreground max-lg:hidden">
        <NavigationLink to="/domains">
          <ArrowLeft /> {t('context.back')}
        </NavigationLink>
      </Button>
    </aside>
  );
}

interface Check {
  id: string;
  ok: boolean | null;
  text: string;
}

/** Renewal preconditions the console can see (the server checks again, including the balance). */
function renewalChecks(detail: DomainDetail, txs: readonly RegistryTransaction[] | null, t: Lookup): Check[] {
  const d = detail.domain;
  const legal = detail.locks.filter((l) => l.lock_type !== 'REGISTRAR_LOCK' && l.lock_type !== 'TRANSFER_LOCK');
  return [
    { id: 'dossier', ok: (detail.dossier?.status ?? d.dossier_state) === 'COMPLETE', text: t('checks.dossier') },
    { id: 'registry', ok: txs === null ? null : unresolvedCount(txs) === 0, text: t('checks.registry') },
    { id: 'state', ok: d.lifecycle_state === 'ACTIVE' || d.lifecycle_state === 'GRACE_PERIOD', text: t('checks.state') },
    { id: 'locks', ok: legal.length === 0, text: t('checks.locks') },
    { id: 'balance', ok: null, text: t('checks.balance') },
  ];
}

/** Why renewal is off, in one sentence (the most important reason). */
function renewBlockedReason(detail: DomainDetail, txs: readonly RegistryTransaction[] | null, t: Lookup, lifecycle: string): string | null {
  const d = detail.domain;
  if (enabledActions(detail).renew) return null;
  if (d.lifecycle_state === 'REDEMPTION' || d.lifecycle_state === 'PENDING_DELETE') return t('actions.redemption', { state: lifecycle });
  if (txs && unresolvedCount(txs) > 0) return t('actions.unresolved');
  return t('actions.notActive', { state: lifecycle });
}

/** "Thao tác tên miền" (every action, renewal first) and the panels under it, in the right column. */
export function ActionRail({ detail, txs, onChanged }: { detail: DomainDetail; txs: readonly RegistryTransaction[] | null; onChanged: () => void }) {
  const { t } = useTranslation('domainPage');
  const tr = t as unknown as Lookup;
  const label = useStatusLabel();
  const d = detail.domain;
  const has = (st: string) => detail.registry_statuses.some((s) => s.status === st);
  const locked = has('clientTransferProhibited');
  const checks = renewalChecks(detail, txs, tr);
  return (
    <aside className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-20" aria-label={t('actions.title')} data-action-rail>
      <RailCard title={t('actions.title')}>
        <DomainActions detail={detail} onChanged={onChanged} renewBlocked={renewBlockedReason(detail, txs, tr, label(LIFECYCLE, d.lifecycle_state))} />
      </RailCard>
      <RailCard title={t('actions.statusTitle')} subtitle={t('actions.subtitle')}>
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{t('actions.autoRenew')}</span>
          <Badge tone={d.auto_renew ? 'ok' : 'neutral'} icon={false}>
            {d.auto_renew ? t('actions.autoRenewOn', { years: d.auto_renew_period }) : t('actions.off')}
          </Badge>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{t('actions.transferLock')}</span>
          <Badge tone={locked ? 'ok' : 'neutral'} icon={false}>
            {locked ? t('actions.on') : t('actions.off')}
          </Badge>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{t('actions.hold')}</span>
          <Badge tone={has('clientHold') ? 'warn' : 'neutral'} icon={false}>
            {has('clientHold') ? t('actions.on') : t('actions.off')}
          </Badge>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{t('actions.updateLock')}</span>
          <Badge tone={has('clientUpdateProhibited') ? 'ok' : 'neutral'} icon={false}>
            {has('clientUpdateProhibited') ? t('actions.on') : t('actions.off')}
          </Badge>
        </div>
      </RailCard>
      <RailCard title={t('checks.title')} subtitle={t('checks.subtitle')}>
        <ul className="grid gap-2.5" data-renewal-checks>
          {checks.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              {c.ok === true ? (
                <Check className="mt-0.5 size-3.5 shrink-0 text-success" aria-label={t('checks.met')} />
              ) : c.ok === false ? (
                <CircleAlert className="mt-0.5 size-3.5 shrink-0 text-warning" aria-label={t('checks.notMet')} />
              ) : (
                <Clock className="mt-0.5 size-3.5 shrink-0" aria-label={t('checks.later')} />
              )}
              <span className={cn(c.ok === false && 'text-foreground')}>{c.text}</span>
            </li>
          ))}
        </ul>
      </RailCard>
      <RailCard title={t('record.title')} className="max-md:hidden">
        <dl className="grid gap-2 text-xs">
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('record.version')}</dt>
            <dd className="font-medium tabular">v{d.version}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('record.updated')}</dt>
            <dd className="text-right font-medium">{formatDateTime(d.updated_at)}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('record.unresolved')}</dt>
            <dd className="font-medium tabular">{txs === null ? '…' : unresolvedCount(txs)}</dd>
          </div>
        </dl>
        <Button asChild variant="outline" size="sm" className="mt-3 w-full justify-start">
          <Link to={domainTabUrl(d.ascii_name, 'history')}>
            <ChevronRight /> {t('record.history')}
          </Link>
        </Button>
      </RailCard>
    </aside>
  );
}
